import React, { useState, useEffect, useId, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { submitEnquiry, readUtmParams } from "../lib/api";
import { enquiryTypes, apartmentPreferences, experiences } from "../data/site";

const requiresApartment = (t) => ["ubytovani", "vinarsky_pobyt"].includes(t);
const requiresExperience = (t) => ["degustace", "piknik", "firemni_akce", "soukroma_oslava", "sezonni_zazitek"].includes(t);
const requiresDates = (t) => ["ubytovani", "vinarsky_pobyt"].includes(t);
const requiresSingleDate = (t) => ["degustace", "piknik", "firemni_akce", "soukroma_oslava", "sezonni_zazitek"].includes(t);
const requiresGuests = (t) => t && t !== "objednavka_vina" && t !== "jiny_dotaz";

// Central default form state so we never depend on prop identity.
function makeInitial({ type = "", experience = "", apartment = "", adults = "2", children = "0" }) {
  return {
    type,
    experience_slug: experience,
    apartment_preference: apartment,
    name: "",
    email: "",
    phone: "",
    adults: String(adults),
    children: String(children),
    children_ages: "",
    arrival_date: "",
    departure_date: "",
    preferred_date: "",
    message: "",
    consent_privacy: false,
    website: "",
  };
}

export default function EnquiryForm({ defaultType = "", defaultExperience = "", defaultApartment = "", defaultAdults, defaultChildren }) {
  const loc = useLocation();
  const uid = useId();

  // Merge URL query params on mount (so CTA links can prefill).
  const urlDefaults = useMemo(() => {
    const p = new URLSearchParams(loc.search);
    return {
      type: p.get("type") || defaultType || "",
      experience: p.get("experience") || defaultExperience || "",
      apartment: p.get("apartment") || defaultApartment || "",
      adults: defaultAdults ?? "2",
      children: defaultChildren ?? "0",
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [form, setForm] = useState(() => makeInitial(urlDefaults));
  const [status, setStatus] = useState({ loading: false, ok: false, error: "", ref: "" });

  // If parent-provided defaults change AFTER mount (e.g. user navigates
  // between two detail pages without unmount), sync them in but keep the
  // user's already-typed values.
  useEffect(() => {
    setForm((prev) => ({
      ...prev,
      type: prev.type || defaultType,
      experience_slug: prev.experience_slug || defaultExperience,
      apartment_preference: prev.apartment_preference || defaultApartment,
    }));
  }, [defaultType, defaultExperience, defaultApartment]);

  const update = (k) => (e) => {
    const el = e?.target;
    const v = el?.type === "checkbox" ? el.checked : el?.value ?? "";
    setForm((prev) => ({ ...prev, [k]: v }));
  };

  const validate = () => {
    if (!form.type) return "Vyberte prosím typ poptávky.";
    if (!form.name.trim()) return "Vyplňte prosím jméno.";
    if (!/^\S+@\S+\.\S+$/.test(form.email)) return "Vyplňte prosím platný e-mail.";
    if (!form.phone.trim() || form.phone.trim().length < 6) return "Vyplňte prosím telefon.";
    if (requiresDates(form.type)) {
      if (!form.arrival_date) return "Vyplňte prosím datum příjezdu.";
      if (!form.departure_date) return "Vyplňte prosím datum odjezdu.";
      if (form.departure_date <= form.arrival_date) return "Datum odjezdu musí být po datu příjezdu.";
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setStatus({ loading: false, ok: false, error: err, ref: "" }); return; }
    if (status.loading) return;

    setStatus({ loading: true, ok: false, error: "", ref: "" });

    // Build payload – always include core fields, only include contextual ones when relevant.
    const p = {
      type: form.type,
      name: form.name.trim(),
      email: form.email.trim(),
      phone: form.phone.trim(),
      message: form.message.trim(),
      consent_privacy: true,
      website: form.website,
      ...readUtmParams(),
    };
    if (requiresApartment(form.type) && form.apartment_preference) p.apartment_preference = form.apartment_preference;
    if (requiresExperience(form.type) && form.experience_slug) p.experience_slug = form.experience_slug;
    if (requiresGuests(form.type)) {
      p.adults = parseInt(form.adults, 10) || 0;
      p.children = parseInt(form.children, 10) || 0;
      if (p.children > 0 && form.children_ages) p.children_ages = form.children_ages;
    }
    if (requiresDates(form.type)) { p.arrival_date = form.arrival_date; p.departure_date = form.departure_date; }
    if (requiresSingleDate(form.type) && form.preferred_date) p.preferred_date = form.preferred_date;

    const res = await submitEnquiry(p);
    if (res.ok) {
      setStatus({ loading: false, ok: true, error: "", ref: res.data?.reference || "" });
      return;
    }
    // Never show raw Axios / HTTP errors to visitors.
    setStatus({
      loading: false, ok: false,
      error: "Poptávku se nepodařilo odeslat. Zkuste to prosím znovu nebo nám zavolejte.",
      ref: "",
    });
  };

  if (status.ok) {
    return (
      <div className="bg-cream border border-burgundy/20 p-6" role="status" data-testid="enquiry-success">
        <div className="font-serif text-2xl text-burgundy">Poptávka byla odeslána.</div>
        <p className="mt-2 text-charcoal/80">Ozveme se vám s potvrzením dostupnosti, ceny a dalších detailů.</p>
        {status.ref && (
          <p className="mt-3 text-sm text-charcoal/70">
            Referenční číslo: <span className="font-serif text-burgundy" data-testid="enquiry-reference">{status.ref}</span>
          </p>
        )}
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4 md:space-y-5" data-testid="enquiry-form" noValidate>
      <input type="text" name="website" value={form.website} onChange={update("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

      <div className="grid md:grid-cols-2 gap-4">
        <Field id={`${uid}-type`} label="Typ poptávky" required>
          <select id={`${uid}-type`} name="type" value={form.type} onChange={update("type")} data-testid="form-type" className="vb-input">
            <option value="">Vyberte typ poptávky</option>
            {enquiryTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </Field>

        {requiresApartment(form.type) && (
          <Field id={`${uid}-apt`} label="Preferovaný apartmán">
            <select id={`${uid}-apt`} name="apartment_preference" value={form.apartment_preference} onChange={update("apartment_preference")} data-testid="form-apartment" className="vb-input">
              <option value="">Vyberte…</option>
              {apartmentPreferences.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
          </Field>
        )}
        {requiresExperience(form.type) && (
          <Field id={`${uid}-exp`} label="Konkrétní zážitek">
            <select id={`${uid}-exp`} name="experience_slug" value={form.experience_slug} onChange={update("experience_slug")} data-testid="form-experience" className="vb-input">
              <option value="">Vyberte…</option>
              {experiences.map((exp) => <option key={exp.slug} value={exp.slug}>{exp.title}</option>)}
            </select>
          </Field>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Field id={`${uid}-name`} label="Jméno a příjmení" required>
          <input id={`${uid}-name`} required name="name" autoComplete="name" value={form.name} onChange={update("name")} data-testid="form-name" className="vb-input" />
        </Field>
        <Field id={`${uid}-email`} label="E-mail" required>
          <input id={`${uid}-email`} required type="email" name="email" autoComplete="email" value={form.email} onChange={update("email")} data-testid="form-email" className="vb-input" />
        </Field>
        <Field id={`${uid}-phone`} label="Telefon" required>
          <input id={`${uid}-phone`} required type="tel" name="phone" autoComplete="tel" value={form.phone} onChange={update("phone")} data-testid="form-phone" className="vb-input" />
        </Field>
      </div>

      {requiresGuests(form.type) && (
        <div className="grid md:grid-cols-3 gap-4">
          <Field id={`${uid}-adults`} label="Počet dospělých">
            <input id={`${uid}-adults`} type="number" name="adults" min="1" inputMode="numeric" value={form.adults} onChange={update("adults")} data-testid="form-adults" className="vb-input" />
          </Field>
          <Field id={`${uid}-children`} label="Počet dětí">
            <input id={`${uid}-children`} type="number" name="children" min="0" inputMode="numeric" value={form.children} onChange={update("children")} data-testid="form-children" className="vb-input" />
          </Field>
          {parseInt(form.children, 10) > 0 && (
            <Field id={`${uid}-ages`} label="Věk dětí">
              <input id={`${uid}-ages`} name="children_ages" value={form.children_ages} onChange={update("children_ages")} placeholder="např. 3 a 6 let" data-testid="form-children-ages" className="vb-input" />
            </Field>
          )}
        </div>
      )}

      {requiresDates(form.type) && (
        <div className="grid md:grid-cols-2 gap-4">
          <Field id={`${uid}-arr`} label="Příjezd" required>
            <input id={`${uid}-arr`} type="date" name="arrival_date" value={form.arrival_date} onChange={update("arrival_date")} data-testid="form-arrival" className="vb-input" />
          </Field>
          <Field id={`${uid}-dep`} label="Odjezd" required>
            <input id={`${uid}-dep`} type="date" name="departure_date" value={form.departure_date} onChange={update("departure_date")} data-testid="form-departure" className="vb-input" />
          </Field>
        </div>
      )}

      {requiresSingleDate(form.type) && (
        <Field id={`${uid}-pdate`} label="Preferované datum">
          <input id={`${uid}-pdate`} type="date" name="preferred_date" value={form.preferred_date} onChange={update("preferred_date")} data-testid="form-preferred-date" className="vb-input" />
        </Field>
      )}

      <Field id={`${uid}-msg`} label="Zpráva">
        <textarea id={`${uid}-msg`} rows={4} name="message" value={form.message} onChange={update("message")} placeholder="Napište termín, počet osob a představu…" data-testid="form-message" className="vb-input" />
      </Field>

      <label className="flex items-start gap-3 text-sm text-charcoal/70" data-testid="form-privacy-notice">
        <span>
          Odesláním formuláře berete na vědomí zpracování uvedených údajů za účelem vyřízení vaší poptávky.
          Podrobnosti najdete v&nbsp;<Link to="/ochrana-osobnich-udaju" className="underline hover:text-burgundy">ochraně osobních údajů</Link>.
        </span>
      </label>

      {status.error && <div className="text-sm text-red-700" role="alert" data-testid="form-error">{status.error}</div>}

      <button disabled={status.loading} type="submit" data-testid="form-submit" className="bg-burgundy hover:bg-burgundy-hover text-ivory px-8 py-3.5 tracking-wide disabled:opacity-60">
        {status.loading ? "Odesílám…" : "Odeslat poptávku"}
      </button>

      <style>{`.vb-input{width:100%;background:#FCFAF6;border:1px solid rgba(37,35,31,.18);padding:.65rem .8rem;font-size:.95rem;color:#25231F;border-radius:2px}.vb-input:focus{outline:2px solid #B6955A;outline-offset:1px}`}</style>
    </form>
  );
}

// Field renders label + child. The label is linked to the child via htmlFor
// so clicking the label focuses the input without any state side-effects.
function Field({ id, label, required, children }) {
  return (
    <div className="block">
      <label htmlFor={id} className="block text-[11px] tracking-[.2em] uppercase text-charcoal/60 mb-1.5">
        {label}{required && <span className="text-burgundy"> *</span>}
      </label>
      {children}
    </div>
  );
}
